/**
 * The brand filter every prospects page shares, and its tie to the workspace.
 *
 * Ported from radar `src/components/Shell.tsx` (the `useBrand()` context) and
 * the brand half of `src/components/GojiSidebar.tsx` with
 * `src/lib/goji-frame.ts`. The rules themselves are in `brand-model.ts`; this
 * file holds the state and the two effects that keep it in step with the
 * workspace.
 *
 * WHAT A PAGE GETS, the same names the old context gave it, so a page ports
 * with its reads unchanged:
 *
 *   me          who is signed in, from GET /me (null until it arrives)
 *   brands      the brands this person sees, enabled ones only
 *   selected    the filter's keys, never empty once brands have loaded
 *   all         every one of the person's brands is in the filter
 *   brandParam  `all` or `a,b`, for ?brand= on every request
 *   current     the one brand in the filter, when it is exactly one
 *   one         where a page needs exactly one: the picked one, else the first
 *   brand       `one`'s key
 *   brandOf     a brand by its key, enabled or not
 *   setSelected change the filter: it is written into ?brand= (dropping
 *               `opp`, a card that may not be in the new list), and remembered
 *   reload      read the brands again (they are read again each minute anyway)
 *   reloadMe    read GET /me again, after a page changed the person's settings
 *   toast       a short note, through this app's own toasts
 *
 * THE WORKSPACE. This app's active brand and the filter follow each other, as
 * the frame bridge made them do when the pages were another app's
 * (owner, 2026-10-23: "they must be synced"). The workspace changing makes the
 * filter show the brands linked to it. A page opened on a link to one brand
 * switches the workspace to that brand's, once. Picking exactly one linked
 * brand switches the workspace, leaving the every-brand view first if it is on,
 * exactly as the workspace menu does.
 */
import { createContext, createElement, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'
import { useToast } from '../../../components/Toast'
import { useBrandSwitch, useSession, useViewSwitch } from '../../../lib/session'
import { prospectingApi, prospectingQueryKeys, prospectingRetry } from './api'
import {
  BRAND_STORAGE_KEY,
  brandParamOf,
  gojiOf,
  linkedBrand,
  onWorkspaceBrand,
  parseBrandParam,
  selectedKeys,
  workspaceBrandId,
  type BrandWant,
} from './brand-model'
import type { BrandRow, Me } from './types'

export interface ProspectingBrandState {
  me: Me | null
  brands: BrandRow[]
  selected: string[]
  all: boolean
  brandParam: string
  current: BrandRow | null
  one: BrandRow | null
  brand: string
  brandOf: (key: string) => BrandRow | undefined
  setSelected: (keys: string[] | 'all') => void
  reload: () => void
  reloadMe: () => void
  toast: (message: string) => void
  /** The brands have not arrived yet. */
  isLoading: boolean
  /** Why the brands could not be read, when they could not. */
  error: unknown
}

const BrandContext = createContext<ProspectingBrandState | null>(null)

/** The brand filter. Only inside the prospects pages, which `ProspectingAccess` wraps in the provider. */
export function useProspectingBrand(): ProspectingBrandState {
  const state = useContext(BrandContext)
  if (state === null) throw new Error('useProspectingBrand must be used inside ProspectingBrandProvider (the prospects pages layout)')
  return state
}

function readStored(): BrandWant | null {
  try {
    return parseBrandParam(window.localStorage.getItem(BRAND_STORAGE_KEY))
  } catch {
    return null
  }
}

function store(want: BrandWant): void {
  try {
    window.localStorage.setItem(BRAND_STORAGE_KEY, brandParamOf(want))
  } catch {
    // Storage refused (a private window): the address still carries the choice.
  }
}

/** GET /me, shared by the access gate and every page. */
export function useProspectingMe() {
  return useQuery({
    queryKey: prospectingQueryKeys.me(),
    queryFn: ({ signal }) => prospectingApi<Me>('/me', { signal }),
    staleTime: 60_000,
    retry: prospectingRetry,
  })
}

/** GET /brands, read again each minute as the old shell did. */
export function useProspectingBrands(enabled = true) {
  return useQuery({
    queryKey: prospectingQueryKeys.brands(),
    queryFn: ({ signal }) => prospectingApi<{ brands: BrandRow[] }>('/brands', { signal }),
    enabled,
    refetchInterval: 60_000,
    staleTime: 30_000,
    retry: prospectingRetry,
  })
}

export function ProspectingBrandProvider({ children }: { children: ReactNode }) {
  const [params, setParams] = useSearchParams()
  const client = useQueryClient()
  const { toast: showToast } = useToast()
  const { session, brands: workspaceBrands, activeBrand, superAdminView } = useSession()
  const switchBrand = useBrandSwitch()
  const changeView = useViewSwitch()

  const meQuery = useProspectingMe()
  const brandsQuery = useProspectingBrands()
  const every = useMemo(() => brandsQuery.data?.brands ?? [], [brandsQuery.data])
  const shown = useMemo(() => every.filter((brand) => brand.enabled !== false), [every])

  // ------------------------------------------------------------ the choice
  const fromUrl = params.get('brand')
  const [want, setWant] = useState<BrandWant>(() => parseBrandParam(fromUrl) ?? readStored() ?? 'all')
  useEffect(() => {
    const asked = parseBrandParam(fromUrl)
    if (asked === null) return
    setWant(asked)
    store(asked)
  }, [fromUrl])

  const selected = useMemo(() => selectedKeys(want, shown), [want, shown])
  const all = selected.length === shown.length
  const brandParam = all ? 'all' : selected.join(',')

  const setSelected = useCallback(
    (keys: string[] | 'all') => {
      const next: BrandWant = keys === 'all' || keys.length === 0 ? 'all' : keys
      setWant(next)
      store(next)
      setParams(
        (previous) => {
          const query = new URLSearchParams(previous)
          query.set('brand', brandParamOf(next))
          query.delete('opp')
          return query
        },
        { replace: true },
      )
    },
    [setParams],
  )

  const brandOf = useCallback((key: string) => every.find((brand) => brand._id === key), [every])
  const current = selected.length === 1 ? (brandOf(selected[0] ?? '') ?? null) : null
  const one = current ?? brandOf(selected[0] ?? '') ?? null

  // ------------------------------------------------------------ the workspace
  const workspace = workspaceBrandId({ superAdminView, activeBrandId: activeBrand?.id ?? null })
  // The brand the address named when the page opened: it wins over the workspace once.
  const [linked] = useState(() => linkedBrand(fromUrl))
  // The workspace brand last handled, so a refetch of the brands does not count as the workspace speaking.
  const seen = useRef<string | null | undefined>(undefined)
  // The workspace brand both sides show, so neither tells the other what it already shows.
  const agreed = useRef<string | null | undefined>(undefined)

  const latest = useRef({ workspaceBrands, activeBrand, superAdminView, switchBrand, changeView })
  latest.current = { workspaceBrands, activeBrand, superAdminView, switchBrand, changeView }

  /** Switch the workspace to a brand, the same switch as the workspace menu. Quiet: it is what the person just did. */
  const tellWorkspace = useCallback((gojiBrandId: string) => {
    agreed.current = gojiBrandId
    const now = latest.current
    const target = now.workspaceBrands.find((brand) => brand.id === gojiBrandId)
    if (target === undefined || now.switchBrand.isPending || now.changeView.isPending) return
    const go = () => {
      if (target.id !== latest.current.activeBrand?.id) latest.current.switchBrand.mutate(target.id)
    }
    if (now.superAdminView) now.changeView.mutate('admin', { onSuccess: go })
    else go()
  }, [])

  // The workspace named its brand, or the brands arrived: the filter follows.
  useEffect(() => {
    if (session === undefined || shown.length === 0) return
    if (seen.current === workspace) return
    const initial = seen.current === undefined
    seen.current = workspace
    // The workspace moving because a brand was picked here is that pick coming back, not a new choice.
    if (!initial && agreed.current === workspace) return
    const step = onWorkspaceBrand(shown, { id: workspace, initial }, linked)
    if ('tell' in step) {
      tellWorkspace(step.tell)
      return
    }
    agreed.current = workspace
    if ('keep' in step) return
    if (selectedKeys(step.follow, shown).join(',') !== selected.join(',')) setSelected(step.follow)
    // Only when the workspace speaks or the brands first arrive, never on this page's own change.
  }, [workspace, shown, session])

  // One linked brand picked here: the workspace switches to it.
  useEffect(() => {
    if (agreed.current === undefined || shown.length === 0) return
    const goji = gojiOf(shown, selected)
    if (goji !== null && goji !== agreed.current) tellWorkspace(goji)
  }, [brandParam])

  // ------------------------------------------------------------ the rest
  const reload = useCallback(() => void client.invalidateQueries({ queryKey: prospectingQueryKeys.brands() }), [client])
  const reloadMe = useCallback(() => void client.invalidateQueries({ queryKey: prospectingQueryKeys.me() }), [client])
  const toast = useCallback((message: string) => void showToast({ title: message }), [showToast])

  const value: ProspectingBrandState = {
    me: meQuery.data ?? null,
    brands: shown,
    selected,
    all,
    brandParam,
    current,
    one,
    brand: one?._id ?? '',
    brandOf,
    setSelected,
    reload,
    reloadMe,
    toast,
    isLoading: brandsQuery.isPending,
    error: brandsQuery.error,
  }
  return createElement(BrandContext.Provider, { value }, children)
}

/** For a test or an isolated render: the context with a value of the caller's choosing. */
export function ProspectingBrandContextForTests({ value, children }: { value: ProspectingBrandState; children: ReactNode }) {
  return createElement(BrandContext.Provider, { value }, children)
}
